import { Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UserPermissionType } from "@apiserver-rest/ponder-auth-client-base";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CreateDeckResponse, DeckFileFormat, GameType, ImportDeckRequest, ImportPreconDeckRequest, ViaCollection, VisibilityType } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { DeckFormatInfos } from "@ponder-shared/utils/deckFormatInfo";
import { DeckTypes } from "@ponder-shared/utils/deckTypes";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { VisibilityTypesReversed } from "@ponder-shared/utils/visbilityTypes";
import { Subscription, take } from "rxjs";
import { getDeckRoute } from "../../deck-router";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "deck-import-modal",
    imports: [BusySpinComponent, ReactiveFormsModule],
    templateUrl: "./deck-import-modal.component.html"
})
export class DeckImportModalComponent {
    @ViewChild("importModalContainer") private importModalContainerRef!: ElementRef<HTMLElement>;

    public isImporting: boolean = false;
    public importFailed: boolean = false;
    public errorMessage?: string;

    public isVisible: boolean = false;
    public isPreconCheckboxVisible: boolean = false;
    public isPrecon: boolean = false;
    public dateOverride?: Date;

    public importDeckForm!: FormGroup;
    public currColor: string = "#292837";
    public prevColor: string | undefined;
    public deckTypes: string[] = DeckTypes.get(GameType.Magic)!;
    public deckGroups: string[] = [];
    
    public readonly allFormats: DeckFileFormat[] = Object.keys(DeckFileFormat) as DeckFileFormat[];
    public readonly visibilityTypes = VisibilityTypesReversed;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly undoSvgIcon: string = SvgIconMap.get(SvgIcon.Undo)!;
    
    private showModalSub!: Subscription;

    public constructor(formBuilder: FormBuilder, private ponderDataClient: PonderDataClient, private events: EventBroker, private authService: AuthService, private navigation: NavigationService) {
        this.importDeckForm = formBuilder.group({
            name: ["", [Validators.required, Validators.minLength(3)]],
            group: ["Default", [Validators.required, Validators.minLength(3)]],
            game: [GameType.Magic, Validators.required],
            type: ["", Validators.required],
            visibility: [VisibilityType.Private, Validators.required],
            color: [this.currColor, [Validators.required, Validators.minLength(7), Validators.maxLength(7)]],
            format: [DeckFileFormat.Moxfield, Validators.required],
            content: [DeckFormatInfos.get(DeckFileFormat.Moxfield)!.sample, [Validators.required, Validators.minLength(3)]],
            cardAction: ["none", Validators.required]
        });
        
        this.events.deck.showDeckImportModalEvent.subscribe({
            next: () => {
                this.onOpen();
            }
        });
    }

    public ngOnDestroy(): void {
        this.showModalSub?.unsubscribe();
    }

    public ngOnInit(): void {
        this.authService.getRoleAndPermissions().then((roleAndPermissions: RoleAndPermissions | undefined) => {
            if (roleAndPermissions) {
                this.isPreconCheckboxVisible = roleAndPermissions.hasPermission(UserPermissionType.EditMagic);
            }
        });

        this.importDeckForm.get("format")?.valueChanges.subscribe((format: DeckFileFormat | null) => {
            if (format) {
                this.onFormatChange(format);
            }
        });
    }

    private onOpen(): void {
        this.isVisible = true;
    }

    public onClose(): void {
        this.isVisible = false;
        this.importFailed = false;
    }
    
    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.isVisible && !this.importModalContainerRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.isVisible && this.mouseDownOutside && !this.importModalContainerRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }
    
    // -------------------------------------------------------------------------------------
    // On Value Change
    // -------------------------------------------------------------------------------------

    public onGameChange(value: string): void {
        this.deckTypes = DeckTypes.get(value as GameType)!;
        this.importDeckForm.patchValue({ type: this.deckTypes[0] });
    }

    public onColorChange(value: string): void {
        this.prevColor = this.currColor;
        this.currColor = value;
    }

    public onUndoColorClicked(): void {
        if (this.prevColor) {
            this.importDeckForm.patchValue({ color: this.prevColor });
            this.currColor = this.prevColor;
            this.prevColor = undefined;
        }
    }
    
    public getFileFormatDisplayName(format: DeckFileFormat): string {
        return DeckFormatInfos.get(format)?.displayName ?? "";
    }

    public onFormatChange(format: DeckFileFormat): void {
        if (format != DeckFileFormat.Unknown) {
            var info = DeckFormatInfos.get(format);
            this.importDeckForm.patchValue({ content: info?.sample ?? "" });
        }
    }
    
    // -------------------------------------------------------------------------------------
    // Import
    // -------------------------------------------------------------------------------------

    public canImport(): boolean {
        return this.isVisible && !this.isImporting && this.importDeckForm.valid && !this.importFailed;
    }

    public onImportClicked(): void {
        if (!this.canImport()) {
            return;
        }

        this.isImporting = true;
        this.importFailed = false;

        if (!this.isPrecon) {
            this.importDeck();
        } else {
            this.importPreconDeck();
        }
    }

    private importDeck(): void {
        var request = new ImportDeckRequest({
            name: this.importDeckForm.get("name")!.value,
            game: this.importDeckForm.get("game")!.value,
            type: this.importDeckForm.get("type")!.value,
            visibility: this.importDeckForm.get("visibility")!.value,
            group: this.importDeckForm.get("group")!.value,
            color: this.importDeckForm.get("color")!.value,
            addToCollection: false,
            skipIfCollected: false,
            format: this.importDeckForm.get("format")!.value,
            contents: this.importDeckForm.get("content")!.value
        });

        switch (this.importDeckForm.get("cardAction")!.value) {
            case "all":
                request.addToCollection = true;
                break;

            case "new":
                request.addToCollection = true;
                request.skipIfCollected = true;
                break;

            case "none":
            default:
                break;
        }
        
        this.ponderDataClient.deckImport(request)
            .pipe(take(1))
            .subscribe({
                next: (response: CreateDeckResponse) => {
                    if (response.success && response.deckId && response.owner) {
                        // todo - even on success there can be an error message. typically because not all cards could be added to their collection due to limits.
                        this.navigation.goto(getDeckRoute(response.owner, response.deckId));
                    } else {
                        this.errorMessage = response.error;
                        this.importFailed = true;
                    }
                },
                error: () => {
                    this.importFailed = true;
                }
            })
            .add(() => { this.isImporting = false });
    }

    private importPreconDeck(): void {
        var request = new ImportPreconDeckRequest({
            name: this.importDeckForm.get("name")!.value,
            game: this.importDeckForm.get("game")!.value,
            type: this.importDeckForm.get("type")!.value,
            visibility: this.importDeckForm.get("visibility")!.value,
            group: this.importDeckForm.get("group")!.value,
            color: this.importDeckForm.get("color")!.value,
            addToCollection: true,
            skipIfCollected: true,
            format: this.importDeckForm.get("format")!.value,
            contents: this.importDeckForm.get("content")!.value,
            createDateOverride: this.dateOverride
        });

        switch (this.importDeckForm.get("cardAction")!.value) {
            case "all":
                request.addToCollection = true;
                break;

            case "new":
                request.addToCollection = true;
                request.skipIfCollected = true;
                break;

            case "none":
            default:
                break;
        }
        
        this.ponderDataClient.deckImportPrecon(request)
            .pipe(take(1))
            .subscribe({
                next: (response: CreateDeckResponse) => {
                    if (response.success && response.deckId && response.owner) {
                        this.importDeckForm.patchValue({ name: "", content: ""  });
                    } else {
                        this.errorMessage = response.error;
                        this.importFailed = true;
                    }
                },
                error: () => {
                    this.importFailed = true;
                }
            })
            .add(() => { this.isImporting = false });
    }
}